<template>
  <div>
    <v-row v-if="deal">
      <v-col cols="12" md="6">
        <div class="preview-field">
          <div class="preview-label">{{ $t("DEALS.MODAL.DEAL_NUMBER") }}</div>
          <div class="preview-value">{{ formattedDealCode }}</div>
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="preview-field">
          <div class="preview-label">{{ $t("GENERAL.LABELS.CREATED_BY") }}</div>
          <div class="preview-value">{{ createdByLabel }}</div>
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="preview-field">
          <div class="preview-label">{{ $t("DEALS.MODAL.OWNERS") }}</div>
          <div class="preview-value">
            <div
              v-if="ownerNames.length"
              class="d-flex flex-wrap align-center"
            >
              <v-chip
                v-for="(name, index) in ownerNames"
                :key="`owner-${index}`"
                x-small
                outlined
                color="primary"
                class="ma-0 mr-1 mb-1"
              >
                {{ name }}
              </v-chip>
            </div>
            <template v-else>-</template>
          </div>
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="preview-field">
          <div class="preview-label">
            {{ $t("DEALS.MODAL.PIPELINE") }} / {{ $t("DEALS.MODAL.STAGE") }}
          </div>
          <div class="preview-value">{{ pipelineAndStage }}</div>
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="preview-field">
          <div class="preview-label">{{ $t("DEALS.MODAL.DEAL_NAME") }}</div>
          <div class="preview-value">{{ deal.name || "-" }}</div>
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="preview-field">
          <div class="preview-label">{{ $t("DEALS.MODAL.ORGANIZATION") }}</div>
          <div class="preview-value">{{ (deal.organization && deal.organization.name) || "-" }}</div>
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="preview-field">
          <div class="preview-label">{{ $t("DEALS.MODAL.SUPPLIERS") }}</div>
          <div class="preview-value">
            <div
              v-if="supplierNames.length"
              class="d-flex flex-wrap align-center"
            >
              <v-chip
                v-for="(name, index) in supplierNames"
                :key="`supplier-${index}`"
                x-small
                outlined
                color="primary"
                class="ma-0 mr-1 mb-1"
              >
                {{ name }}
              </v-chip>
            </div>
            <template v-else>-</template>
          </div>
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="preview-field">
          <div class="preview-label">{{ $t("DEALS.MODAL.CLIENTS") }}</div>
          <div class="preview-value">
            <div
              v-if="clientNames.length"
              class="d-flex flex-wrap align-center"
            >
              <v-chip
                v-for="(name, index) in clientNames"
                :key="`client-${index}`"
                x-small
                outlined
                color="primary"
                class="ma-0 mr-1 mb-1"
              >
                {{ name }}
              </v-chip>
            </div>
            <template v-else>-</template>
          </div>
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="preview-field">
          <div class="preview-label">{{ $t("DEALS.MODAL.TYPE") }}</div>
          <div class="preview-value">
            <v-chip
              v-if="deal.deal_type && deal.deal_type.name"
              small
              outlined
              :color="getDealTypeChipColor(deal.deal_type)"
            >
              {{ deal.deal_type.name }}
            </v-chip>
            <template v-else>-</template>
          </div>
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="preview-field">
          <div class="preview-label">{{ $t("DEALS.MODAL.INCOTERM") }}</div>
          <div class="preview-value">{{ (deal.incoterm && deal.incoterm.name) || "-" }}</div>
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="preview-field">
          <div class="preview-label">{{ $t("DEALS.MODAL.LOCATION") }}</div>
          <div class="preview-value">{{ deal.location || "-" }}</div>
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="preview-field">
          <div class="preview-label">{{ $t("DEALS.MODAL.STATUS") }}</div>
          <div class="preview-value">
            <v-select
              v-if="canEditStatus"
              :value="selectedStatusId"
              :items="dealStatusOptions"
              item-text="label"
              item-value="id"
              dense
              outlined
              hide-details
              :loading="isLoadingStatuses || isUpdatingStatus"
              :disabled="isUpdatingStatus || isLoadingStatuses"
              :menu-props="{ closeOnClick: true, closeOnContentClick: true }"
              class="preview-status-select"
              @change="onStatusChange"
            />
            <template v-else>
              {{ (deal.deal_status && deal.deal_status.name) || "-" }}
            </template>
          </div>
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="preview-field">
          <div class="preview-label">{{ $t("DEALS.MODAL.IS_PAID") }}</div>
          <div class="preview-value">
            {{ deal.is_paid ? $t("DEALS.MODAL.PAID_YES") : $t("DEALS.MODAL.PAID_NO") }}
          </div>
        </div>
      </v-col>
      <v-col cols="12">
        <div class="preview-field">
          <div class="preview-label">{{ $t("DEALS.MODAL.PRODUCTS") }}</div>
          <div class="preview-value">
            <template v-if="productLabels.length">
              <v-chip
                v-for="(label, index) in productLabels"
                :key="`product-${index}`"
                small
                class="mr-2 mb-1"
              >
                {{ label }}
              </v-chip>
            </template>
            <template v-else>-</template>
          </div>
        </div>
      </v-col>
      <v-col v-if="isContainerDealType" cols="12" md="6">
        <div class="preview-field">
          <div class="preview-label">{{ $t("DEALS.MODAL.CONTAINER_NUMBER") }}</div>
          <div class="preview-value">{{ deal.container_number || "-" }}</div>
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="preview-field">
          <div class="preview-label">{{ $t("DEALS.MODAL.CONTAINER_ARRIVAL_DATE") }}</div>
          <div class="preview-value">{{ deal.container_arrival_date || "-" }}</div>
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="preview-field">
          <div class="preview-label">{{ $t("DEALS.MODAL.LOADING_DATES") }}</div>
          <div class="preview-value">
            <template v-if="loadingDateLabels.length">
              <v-chip v-for="item in loadingDateLabels" :key="item" class="mr-2 mb-1" small>
                {{ item }}
              </v-chip>
            </template>
            <template v-else>-</template>
          </div>
        </div>
      </v-col>
      <v-col cols="12">
        <div class="preview-field preview-field--note">
          <div class="preview-label">{{ $t("DEALS.MODAL.NOTE") }}</div>
          <div class="preview-value">
            <v-textarea
              v-if="canEditDeal"
              v-model="noteValue"
              dense
              outlined
              hide-details
              rows="2"
              auto-grow
              class="preview-note-input"
              @input="onNoteInput"
            />
            <template v-else>
              {{ deal.note || "-" }}
            </template>
          </div>
        </div>
      </v-col>
    </v-row>

    <Confirm
      v-model="completeConfirmOpen"
      :content="completeConfirmMessage"
      @confirm="confirmCompleteDeal"
      @decline="declineCompleteDeal"
    />
  </div>
</template>

<script>
import { debounce } from "lodash";
import Confirm from "@/views/partials/dialogs/Confirm.vue";
import DealsService from "@/common/deals.service";
import { SHOW_SNACK } from "@/store/snack.module";
import { UPDATE_DEAL_FIELD } from "@/store/deals.module";
import { GET_DEAL_STATUS_OPTIONS } from "@/store/dealStatuses.module";
import { GET_DEAL_DOCUMENT_TYPE_OPTIONS } from "@/store/dealDocumentTypes.module";

export default {
  name: "DealModalInformation",
  components: {
    Confirm,
  },
  props: {
    deal: {
      type: Object,
      default: null,
    },
    documents: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      dealStatusOptions: [],
      documentTypes: [],
      isLoadingStatuses: false,
      isUpdatingStatus: false,
      selectedStatusId: null,
      noteValue: "",
      noteDirty: false,
      isSavingNote: false,
      completeConfirmOpen: false,
      completeConfirmMessage: "",
      pendingStatusId: null,
    };
  },
  computed: {
    canEditDeal() {
      return this.hasPermissions("deals-edit") && !!this.deal?.id;
    },
    isCompletedDeal() {
      return this.deal?.deal_status?.slug === "completed";
    },
    canEditStatus() {
      return (
        this.canEditDeal &&
        (!this.isCompletedDeal || this.hasPermissions("deals-completed-edit"))
      );
    },
    formattedDealCode() {
      return DealsService.formatDealCode(this.deal || {});
    },
    createdByLabel() {
      return this.deal?.created_by?.name || this.deal?.created_by?.label || "-";
    },
    ownerNames() {
      return (this.deal?.owners || [])
        .map((item) => item.name || item.label)
        .filter(Boolean);
    },
    pipelineAndStage() {
      const pipelineName =
        (this.deal.pipeline_stage &&
          this.deal.pipeline_stage.pipeline &&
          this.deal.pipeline_stage.pipeline.name) ||
        "";
      const stageName = (this.deal.pipeline_stage && this.deal.pipeline_stage.name) || "";

      if (pipelineName && stageName) {
        return `${pipelineName} - ${stageName}`;
      }

      return pipelineName || stageName || "-";
    },
    productLabels() {
      return DealsService.getProductLabels(this.deal);
    },
    supplierNames() {
      return (this.deal.suppliers || []).map((item) => item.name).filter(Boolean);
    },
    clientNames() {
      return (this.deal.clients || []).map((item) => item.name).filter(Boolean);
    },
    loadingDateLabels() {
      return (this.deal.loading_dates || [])
        .map((item) => DealsService.formatLoadingDate(item))
        .filter((label) => label && label !== "-");
    },
    isContainerDealType() {
      return this.deal?.deal_type?.slug === "container";
    },
  },
  watch: {
    deal: {
      immediate: true,
      handler(deal) {
        this.selectedStatusId = deal?.deal_status?.id || null;

        if (!this.noteDirty && !this.isSavingNote) {
          this.noteValue = deal?.note || "";
        }
      },
    },
    canEditStatus: {
      immediate: true,
      handler(canEdit) {
        if (canEdit && !this.dealStatusOptions.length && !this.isLoadingStatuses) {
          this.loadStatusOptions();
        }
      },
    },
  },
  created() {
    this.debouncedSaveNote = debounce(this.saveNote, 1000);
  },
  beforeDestroy() {
    if (this.debouncedSaveNote) {
      this.debouncedSaveNote.flush();
    }
  },
  methods: {
    getDealTypeChipColor(dealType) {
      return DealsService.getDealTypeChipColor(dealType);
    },
    getErrorMessage(error) {
      if (error && error.errors && typeof error.errors === "object") {
        const firstErrorField = Object.keys(error.errors)[0];
        const firstErrorList = error.errors[firstErrorField];

        if (Array.isArray(firstErrorList) && firstErrorList.length) {
          return firstErrorList[0];
        }
      }

      if (error && error.message) {
        return error.message;
      }

      return this.$t("GENERAL.UNEXPECTED_ERROR");
    },
    loadStatusOptions() {
      this.isLoadingStatuses = true;

      this.$store
        .dispatch(GET_DEAL_STATUS_OPTIONS)
        .then((response) => {
          this.dealStatusOptions = Array.isArray(response?.data) ? response.data : [];
        })
        .catch((error) => {
          this.dealStatusOptions = [];
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        })
        .finally(() => {
          this.isLoadingStatuses = false;
        });
    },
    async ensureDocumentTypesLoaded() {
      if (this.documentTypes.length) {
        return this.documentTypes;
      }

      const response = await this.$store.dispatch(GET_DEAL_DOCUMENT_TYPE_OPTIONS);
      this.documentTypes = Array.isArray(response?.data) ? response.data : [];
      return this.documentTypes;
    },
    isMovingToCompletedStatus(dealStatusId) {
      const completedStatus = this.dealStatusOptions.find((item) => item.slug === "completed");

      if (!completedStatus || Number(dealStatusId) !== Number(completedStatus.id)) {
        return false;
      }

      return Number(this.deal?.deal_status?.id) !== Number(completedStatus.id);
    },
    async onStatusChange(dealStatusId) {
      if (
        !this.canEditStatus ||
        !dealStatusId ||
        Number(dealStatusId) === Number(this.deal?.deal_status?.id) ||
        this.isUpdatingStatus
      ) {
        this.selectedStatusId = this.deal?.deal_status?.id || null;
        return;
      }

      if (this.isMovingToCompletedStatus(dealStatusId)) {
        try {
          await this.ensureDocumentTypesLoaded();
          const missingDocuments = DealsService.getMissingRequiredDocumentNames(
            this.documentTypes,
            this.documents
          );

          if (missingDocuments.length) {
            this.pendingStatusId = dealStatusId;
            this.completeConfirmMessage = this.$t(
              "DEALS.MODAL.COMPLETE_MISSING_DOCUMENTS_CONFIRM",
              {
                documents: missingDocuments.join(", "),
              }
            );
            this.completeConfirmOpen = true;
            this.selectedStatusId = this.deal?.deal_status?.id || null;
            return;
          }
        } catch (error) {
          this.selectedStatusId = this.deal?.deal_status?.id || null;
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
          return;
        }
      }

      this.updateStatus(dealStatusId);
    },
    confirmCompleteDeal() {
      this.completeConfirmOpen = false;
      const dealStatusId = this.pendingStatusId;
      this.pendingStatusId = null;

      if (!dealStatusId) {
        return;
      }

      this.updateStatus(dealStatusId);
    },
    declineCompleteDeal() {
      this.completeConfirmOpen = false;
      this.pendingStatusId = null;
      this.selectedStatusId = this.deal?.deal_status?.id || null;
    },
    updateStatus(dealStatusId) {
      if (!this.canEditStatus || !this.deal?.id) {
        return;
      }

      this.isUpdatingStatus = true;
      this.selectedStatusId = dealStatusId;

      this.$store
        .dispatch(UPDATE_DEAL_FIELD, {
          dealId: this.deal.id,
          field: "deal_status_id",
          value: dealStatusId,
        })
        .then((deal) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          });
          this.$emit("updated", deal);
        })
        .catch((error) => {
          this.selectedStatusId = this.deal?.deal_status?.id || null;
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        })
        .finally(() => {
          this.isUpdatingStatus = false;
        });
    },
    onNoteInput() {
      if (!this.canEditDeal) {
        return;
      }

      this.noteDirty = true;
      this.debouncedSaveNote();
    },
    saveNote() {
      if (!this.canEditDeal || this.isSavingNote) {
        return;
      }

      const nextNote = (this.noteValue || "").trim() || null;
      const currentNote = this.deal?.note || null;

      if (nextNote === currentNote) {
        this.noteDirty = false;
        return;
      }

      this.isSavingNote = true;

      this.$store
        .dispatch(UPDATE_DEAL_FIELD, {
          dealId: this.deal.id,
          field: "note",
          value: nextNote,
        })
        .then((deal) => {
          this.noteDirty = false;
          this.$emit("field-updated", deal);
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        })
        .finally(() => {
          this.isSavingNote = false;
        });
    },
  },
};
</script>

<style scoped>
.preview-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #f8f9fc;
  border: 1px solid #e3e6ef;
  border-radius: 8px;
  padding: 12px 14px;
  min-height: 56px;
  height: 100%;
  box-sizing: border-box;
}

.preview-label {
  flex: 0 1 auto;
  font-size: 12px;
  text-transform: uppercase;
  color: #7f8899;
  margin-bottom: 0;
  line-height: 1.3;
}

.preview-value {
  flex: 1 1 auto;
  min-width: 0;
  color: #212529;
  font-size: 15px;
  line-height: 1.4;
  word-break: break-word;
  text-align: right;
}

.preview-status-select {
  max-width: 100%;
  margin-top: 0;
  padding-top: 0;
}

.preview-status-select >>> .v-input__control {
  min-height: 0 !important;
}

.preview-status-select >>> .v-input__slot {
  min-height: 32px !important;
  height: 32px !important;
  padding: 0 8px !important;
  background: #fff;
}

.preview-status-select >>> .v-select__selections {
  font-size: 15px;
  line-height: 1.4;
  padding: 0 !important;
  min-height: 32px !important;
  justify-content: flex-end;
}

.preview-status-select >>> .v-select__selection {
  margin: 0 !important;
  max-width: 100%;
}

.preview-status-select >>> .v-input__append-inner {
  margin-top: 4px !important;
  align-self: center;
}

.preview-field--note {
  align-items: flex-start;
}

.preview-field--note .preview-label {
  padding-top: 8px;
}

.preview-note-input {
  width: 100%;
  text-align: left;
}

.preview-note-input >>> .v-input__slot {
  background: #fff;
  margin-bottom: 0 !important;
}

.preview-note-input >>> textarea {
  text-align: left !important;
  line-height: 1.4 !important;
}

@media (max-width: 600px) {
  .preview-field {
    flex-direction: column;
    align-items: flex-start;
  }

  .preview-value {
    width: 100%;
    text-align: left;
  }

  .preview-status-select >>> .v-select__selections {
    justify-content: flex-start;
  }

  .preview-field--note .preview-label {
    padding-top: 0;
  }
}
</style>
